import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { GET_LIST_ORDER_QUERY_KEY } from '@/features/orders/api/get-list-order.api';
import { GET_ORDER_QUERY_KEY } from '@/features/orders/api/get-order.api';
import {
  SetOrderPaidSchema,
  setOrderPaidMutationOptions,
} from '@/features/orders/api/set-order-paid.api';

export interface SetOrderPaidDialogProps extends BaseDialogProps {
  orderId: string;
  invoiceNumber: string;
}

export default function SetOrderPaidDialog({
  dialog,
  orderId,
  invoiceNumber,
}: SetOrderPaidDialogProps) {
  const { closeSetOrderPaidDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const setPaidMutation = useMutation({
    ...setOrderPaidMutationOptions(),
    onSuccess: () => {
      toast.success(`Order ${invoiceNumber} ditandai sebagai dibayar`);
      queryClient.invalidateQueries({ queryKey: [GET_LIST_ORDER_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [GET_ORDER_QUERY_KEY, orderId],
      });
      closeSetOrderPaidDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menandai order sebagai dibayar';
      toast.error(message);
    },
  });

  const form = useForm({
    defaultValues: {
      transfer_proof: undefined as File | undefined,
    },
    onSubmit: async ({ value }) => {
      const parsed = SetOrderPaidSchema.safeParse(value);
      if (!parsed.success) {
        toast.error(
          parsed.error.issues[0]?.message ?? 'Bukti transfer tidak valid'
        );
        return;
      }
      await setPaidMutation.mutateAsync({
        order_id: orderId,
        data: parsed.data,
      });
    },
  });

  const isPending = setPaidMutation.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeSetOrderPaidDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tandai Sebagai Dibayar</DialogTitle>
          <DialogDescription>
            Tandai order {invoiceNumber} sebagai dibayar.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field name="transfer_proof">
            {field => (
              <div className="grid gap-2">
                <Label htmlFor={field.name}>Bukti Transfer</Label>
                <Input
                  id={field.name}
                  type="file"
                  accept="image/*,application/pdf"
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.files?.[0])}
                />
                <p className="text-xs text-muted-foreground">
                  Unggah gambar atau PDF bukti pembayaran untuk menandai order
                  ini sebagai dibayar.
                </p>
              </div>
            )}
          </form.Field>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeSetOrderPaidDialog}
              disabled={isPending}
            >
              Batal
            </Button>
            <form.Subscribe
              selector={state => [state.values.transfer_proof, state.canSubmit]}
            >
              {([file, canSubmit]) => (
                <Button
                  type="submit"
                  disabled={!file || !canSubmit || isPending}
                >
                  {isPending ? 'Memproses...' : 'Tandai Dibayar'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
